import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Heart, Users, Lightbulb, Target } from 'lucide-react';

const AboutPage = () => {
  return (
    <div className="min-h-screen bg-bg-base py-12 px-6">
      <div className="max-w-5xl mx-auto">
        <Link to="/" className="inline-flex items-center gap-2 text-text-secondary hover:text-gold transition-colors mb-8">
          <ArrowLeft size={20} />
          Back to Home
        </Link>
        
        <div className="bg-bg-surface rounded-2xl p-8 lg:p-12 shadow-sm border border-border">
          <h1 className="font-display text-4xl font-bold text-text-primary mb-6">About Daypop</h1>
          
          <div className="prose prose-lg max-w-none space-y-10">
            <section>
              <p className="text-xl text-text-secondary leading-relaxed">
                Daypop is a calm, private place to remember your life as you live it — not for an audience, but for
                you. Log what happened, who mattered, what you learned, and what you&apos;re aiming for next. When things
                move fast, you still have the proof.
              </p>
            </section>

            <section className="bg-gold-light/20 rounded-2xl p-8 border-l-4 border-gold">
              <h2 className="font-display text-2xl font-bold text-text-primary mb-4">Our Mission</h2>
              <p className="text-text-secondary leading-relaxed text-lg">
                To help anyone with a path — careers, families, projects, recovery, reinvention — capture what matters
                before it fades, and see their own progress with clarity instead of doubt.
              </p>
            </section>

            <section>
              <h2 className="font-display text-3xl font-bold text-text-primary mb-6">Why We Built Daypop</h2>
              <p className="text-text-secondary leading-relaxed mb-6">
                Life rarely moves in straight lines. You have good weeks, hard weeks, small wins, and sharp turns — often
                all at once. Without a place to capture them, memory smooths everything into a blur, and it&apos;s easy to
                forget how capable you actually are.
              </p>
              <p className="text-text-secondary leading-relaxed mb-6">
                Generic notes sprawl. Social feeds perform. Spreadsheets feel cold. We wanted something in the middle: a
                private home for your story — structured enough to be useful, human enough to stick with.
              </p>
              <p className="text-text-secondary leading-relaxed">
                Daypop is that home. It helps you notice patterns, keep relationships in context, and look back without
                cringing — because the record is honest, not curated.
              </p>
            </section>

            <section>
              <h2 className="font-display text-3xl font-bold text-text-primary mb-6">What Makes Daypop Different</h2>
              
              <div className="grid md:grid-cols-2 gap-6">
                <div className="bg-teal-light/10 rounded-xl p-6 border border-teal/20">
                  <div className="w-12 h-12 bg-teal-light rounded-xl flex items-center justify-center mb-4">
                    <Heart className="text-teal" size={24} />
                  </div>
                  <h3 className="font-display text-xl font-bold text-text-primary mb-3">Built for real life</h3>
                  <p className="text-text-secondary leading-relaxed">
                    Wins, setbacks, decisions, and lessons live together — so your record matches how days actually feel,
                    not how a template thinks they should.
                  </p>
                </div>

                <div className="bg-lavender-light/10 rounded-xl p-6 border border-lavender/20">
                  <div className="w-12 h-12 bg-lavender-light rounded-xl flex items-center justify-center mb-4">
                    <Users className="text-lavender" size={24} />
                  </div>
                  <h3 className="font-display text-xl font-bold text-text-primary mb-3">Your Network, Mapped</h3>
                  <p className="text-text-secondary leading-relaxed">
                    Keep track of the people who matter — investors, mentors, collaborators, and interesting strangers. 
                    Remember context, plan follow-ups, and stay human with the people who matter.
                  </p>
                </div>

                <div className="bg-amber-light/10 rounded-xl p-6 border border-amber/20">
                  <div className="w-12 h-12 bg-amber-light rounded-xl flex items-center justify-center mb-4">
                    <Lightbulb className="text-amber" size={24} />
                  </div>
                  <h3 className="font-display text-xl font-bold text-text-primary mb-3">Lessons That Stick</h3>
                  <p className="text-text-secondary leading-relaxed">
                    Extract lessons from your experiences and mark when you've actually applied them. 
                    Turn mistakes into wisdom, not just memories.
                  </p>
                </div>

                <div className="bg-sage-light/10 rounded-xl p-6 border border-sage/20">
                  <div className="w-12 h-12 bg-sage-light rounded-xl flex items-center justify-center mb-4">
                    <Target className="text-sage" size={24} />
                  </div>
                  <h3 className="font-display text-xl font-bold text-text-primary mb-3">Goals That Connect</h3>
                  <p className="text-text-secondary leading-relaxed">
                    Set goals that tie back to your journey. Break them into steps, track progress, 
                    and see how they connect to your entries and lessons.
                  </p>
                </div>
              </div>
            </section>

            <section>
              <h2 className="font-display text-3xl font-bold text-text-primary mb-6">Core Features</h2>
              
              <div className="space-y-4">
                <div className="flex gap-4 items-start">
                  <div className="w-8 h-8 bg-gold rounded-lg flex items-center justify-center flex-shrink-0 mt-1">
                    <span className="text-white font-bold text-sm">1</span>
                  </div>
                  <div>
                    <h4 className="font-display text-lg font-bold text-text-primary mb-2">Daily Log</h4>
                    <p className="text-text-secondary">
                      Capture wins, setbacks, meetings, ideas, milestones, and thoughts. Every entry is timestamped 
                      and tagged for easy retrieval.
                    </p>
                  </div>
                </div>

                <div className="flex gap-4 items-start">
                  <div className="w-8 h-8 bg-gold rounded-lg flex items-center justify-center flex-shrink-0 mt-1">
                    <span className="text-white font-bold text-sm">2</span>
                  </div>
                  <div>
                    <h4 className="font-display text-lg font-bold text-text-primary mb-2">Lessons Library</h4>
                    <p className="text-text-secondary">
                      Extract and categorize lessons from your experiences. Mark them as applied when you actually 
                      put them into practice.
                    </p>
                  </div>
                </div>

                <div className="flex gap-4 items-start">
                  <div className="w-8 h-8 bg-gold rounded-lg flex items-center justify-center flex-shrink-0 mt-1">
                    <span className="text-white font-bold text-sm">3</span>
                  </div>
                  <div>
                    <h4 className="font-display text-lg font-bold text-text-primary mb-2">Orbit</h4>
                    <p className="text-text-secondary">
                      Map the people in your professional network. Track where you met, what you discussed, 
                      and when to follow up. Connect with other Daypop users seamlessly.
                    </p>
                  </div>
                </div>

                <div className="flex gap-4 items-start">
                  <div className="w-8 h-8 bg-gold rounded-lg flex items-center justify-center flex-shrink-0 mt-1">
                    <span className="text-white font-bold text-sm">4</span>
                  </div>
                  <div>
                    <h4 className="font-display text-lg font-bold text-text-primary mb-2">Goals & Milestones</h4>
                    <p className="text-text-secondary">
                      Set clear goals with actionable steps. Track progress and see how they connect to your 
                      broader journey.
                    </p>
                  </div>
                </div>

                <div className="flex gap-4 items-start">
                  <div className="w-8 h-8 bg-gold rounded-lg flex items-center justify-center flex-shrink-0 mt-1">
                    <span className="text-white font-bold text-sm">5</span>
                  </div>
                  <div>
                    <h4 className="font-display text-lg font-bold text-text-primary mb-2">Journey Tree (Coming Soon)</h4>
                    <p className="text-text-secondary">
                      A visual map of chapters, entries, and milestones — so you can step back and see your arc at a glance.
                    </p>
                  </div>
                </div>
              </div>
            </section>

            <section className="bg-bg-base/50 rounded-2xl p-8">
              <h2 className="font-display text-3xl font-bold text-text-primary mb-4">Privacy & Security</h2>
              <p className="text-text-secondary leading-relaxed mb-4">
                Your journey is personal. We take privacy seriously:
              </p>
              <ul className="space-y-3 text-text-secondary">
                <li className="flex items-start gap-3">
                  <span className="text-gold font-bold">✓</span>
                  <span>Your data is encrypted and stored securely</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-gold font-bold">✓</span>
                  <span>Row-level security ensures you only see your own data</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-gold font-bold">✓</span>
                  <span>We never sell or share your personal information</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-gold font-bold">✓</span>
                  <span>You own your data and can export it anytime</span>
                </li>
              </ul>
              <p className="text-text-secondary leading-relaxed mt-6">
                Read our full <Link to="/privacy" className="text-gold hover:text-gold-muted transition-colors font-medium">Privacy Policy</Link>
              </p>
            </section>

            <section>
              <h2 className="font-display text-3xl font-bold text-text-primary mb-6">Who Is Daypop For?</h2>
              <div className="space-y-4 text-text-secondary leading-relaxed">
                <p><strong className="text-text-primary">Anyone in motion:</strong> New job, new city, new chapter — keep receipts for who you&apos;re becoming.</p>
                <p><strong className="text-text-primary">People with a long game:</strong> Health, craft, business, caregiving — progress that doesn&apos;t fit a single headline.</p>
                <p><strong className="text-text-primary">Teams of one (for now):</strong> Freelancers, soloists, side-project people — your orbit and wins in one place.</p>
                <p><strong className="text-text-primary">The quietly ambitious:</strong> You don&apos;t need a label. If your days matter to you, Daypop is for you.</p>
              </div>
            </section>

            <section className="text-center bg-gradient-to-br from-gold-light/20 to-teal-light/20 rounded-2xl p-12 border border-gold/20">
              <h2 className="font-display text-3xl font-bold text-text-primary mb-4">Start Your Journey Today</h2>
              <p className="text-text-secondary text-lg mb-8">
                Start free — your story stays private, honest, and yours.
              </p>
              <Link 
                to="/" 
                className="inline-block px-8 py-4 bg-gold hover:bg-gold-muted text-white font-semibold rounded-xl transition-all transform hover:scale-105 shadow-lg"
              >
                Get Started — It's Free
              </Link>
            </section>

            <section className="border-t border-border pt-8">
              <h2 className="font-display text-2xl font-bold text-text-primary mb-4">Questions?</h2>
              <p className="text-text-secondary leading-relaxed">
                We'd love to hear from you. Reach out at <span className="text-gold font-medium">support@daypop.online</span>
              </p>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AboutPage;
